import React from "react";
import { motion } from "framer-motion";

export type Option = string | { label: string; value: string };

interface RadioGroupProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

function getLabel(opt: Option) {
  return typeof opt === "string" ? opt : opt.label;
}

function getValue(opt: Option) {
  return typeof opt === "string" ? opt : opt.value;
}

function getKey(opt: Option, idx: number) {
  const v = getValue(opt);
  const l = getLabel(opt);
  return `${v}__${l}__${idx}`;
}

export function RadioGroup({ options, value, onChange, label }: RadioGroupProps) {
  return (
    <div className="space-y-8">
      {label && (
        <label className="block text-2xl font-normal text-[#2D2A35] mb-8 text-center tracking-tight">
          {label}
        </label>
      )}

      <div className="flex flex-wrap gap-6 justify-center">
        {options.map((opt, idx) => {
          const optValue = getValue(opt);
          const optLabel = getLabel(opt);
          const isSelected = value === optValue;

          return (
            <motion.button
              key={getKey(opt, idx)}
              type="button"
              onClick={() => onChange(optValue)}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className={`
                relative pl-5 pr-8 py-4 rounded-full border text-base font-medium transition-all duration-500 flex items-center gap-4
                ${
                  isSelected
                    ? "border-[#9B87C7] bg-[#F8F6FB] text-[#2D2A35] shadow-lg shadow-[#9B87C7]/10"
                    : "bg-white text-[#6B6575] border-[#E8E4EE] hover:border-[#B5A8D4] hover:text-[#9B87C7] hover:shadow-md"
                }
              `}
            >
              <div
                className={`
                w-5 h-5 rounded-full border flex items-center justify-center transition-colors duration-500 tactile-texture
                ${isSelected ? "border-[#9B87C7] bg-[#9B87C7]" : "border-[#D4CED9] bg-white"}
              `}
              />
              {optLabel}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}